import type { UserCookieData } from "@/types/cookie-banner/props";
import type { FirebaseConfig } from "@/types/cookie-consent-configuration";

import * as CookieConsent from "vanilla-cookieconsent";

import { initializeApp } from "firebase/app";
import { collection, doc, getDoc, getFirestore, setDoc } from "firebase/firestore/lite";
import { useCallback, useMemo } from "react";

export function useSetupFirebase(firebaseConfig: FirebaseConfig = { collectionName: "AFEX" }) {
  const { collectionName, projectId } = firebaseConfig;

  const app = useMemo(() => {
    if (!projectId) return null;

    try {
      return initializeApp(firebaseConfig);
    } catch (error) {
      console.error("Failed to initialize Firebase:", error);
      return null;
    }
  }, [firebaseConfig]);

  const db = useMemo(() => {
    if (!app) return null;
    return getFirestore(app);
  }, [app]);

  const updateUserCookieData = useCallback(
    async (
      data: Partial<UserCookieData>,
      documentId = data.userId,
      onError?: (e: unknown) => void
    ): Promise<null | string> => {
      if (!collectionName || !documentId || !db) return null;

      if (data.userId !== documentId) {
        CookieConsent.setCookieData({
          mode: "update",
          value: { userId: data.userId },
        });
      }

      try {
        const cookieSettingsRef = collection(db, collectionName);
        const newDocRef = doc(cookieSettingsRef, documentId);
        await setDoc(newDocRef, data);
        return newDocRef.id;
      } catch (error) {
        onError?.(error);
        return null;
      }
    },
    [collectionName, db]
  );

  const retrieveUserCookieData = useCallback(
    async (documentId: string, onError?: (e: unknown) => void): Promise<null | UserCookieData> => {
      if (!collectionName || !documentId || !db) return null;

      try {
        const cookieSettingsRef = collection(db, collectionName);
        const docRef = doc(cookieSettingsRef, documentId);
        const docSnapshot = await getDoc(docRef);

        if (!docSnapshot.exists()) return null;
        return { id: docSnapshot.id, ...docSnapshot.data() } as UserCookieData;
      } catch (error) {
        onError?.(error);
        return null;
      }
    },
    [collectionName, db]
  );

  return { retrieveUserCookieData, updateUserCookieData };
}
